/**
 * User-agent matching for automated traffic.
 *
 * Two matchers, because the two callers need different breadth:
 *
 * - `isGoogleRenderer` is the narrow one: only the agents that actually render
 *   the page for Google. Used to skip behaviour that exists to rescue a human
 *   session (see `plugins/00.handleChunkErrors.client.ts`), so a false positive
 *   costs a real user their self-heal. It must not fire on anything but Google.
 * - `isBot` is the broad, permissive list used to zero out RUM sampling. A false
 *   positive there only loses one session's telemetry, which is why it can
 *   afford entries as loose as `bot`, `java` or `speedy`.
 *
 * The broad list matches on the substring `bot`, so it already covered
 * `Googlebot` — but not `Google-InspectionTool`, `GoogleOther`,
 * `Google-Extended` or `Chrome-Lighthouse`, none of which contain it. Building
 * it from the narrow list means those tokens are declared once and both matchers
 * stay in agreement.
 */

/**
 * Agents that fetch and render pages on Google's behalf: the crawler itself,
 * the Search Console URL Inspection renderer, the uncategorised GoogleOther
 * fetcher, the Gemini/Vertex opt-out agent, and PageSpeed Insights/Lighthouse.
 *
 * @see https://developers.google.com/search/docs/crawling-indexing/overview-google-crawlers
 */
const GOOGLE_RENDERER_TOKENS = [
  'Googlebot',
  'Google-InspectionTool',
  'GoogleOther',
  'Google-Extended',
  'Chrome-Lighthouse',
] as const

/**
 * Kept verbatim from the list this was extracted from, minus the Google entries
 * now supplied by GOOGLE_RENDERER_TOKENS. Unescaped `.` and the loose entries
 * are original: widening it is not the point of this file, and narrowing it
 * would change which sessions RUM samples.
 */
const BOT_TOKENS = [
  'googlebot/',
  'bot',
  'Google favicon',
  'Mediapartners-Google',
  'bingbot',
  'slurp',
  'java',
  'wget',
  'curl',
  'Commons-HttpClient',
  'Python-urllib',
  'libwww',
  'httpunit',
  'nutch',
  'phpcrawl',
  'msnbot',
  'jyxobot',
  'FAST-WebCrawler',
  'FAST Enterprise Crawler',
  'biglotron',
  'teoma',
  'convera',
  'seekbot',
  'gigablast',
  'exabot',
  'ngbot',
  'ia_archiver',
  'GingerCrawler',
  'webmon ',
  'httrack',
  'webcrawler',
  'grub.org',
  'UsineNouvelleCrawler',
  'antibot',
  'netresearchserver',
  'speedy',
  'fluffy',
  'bibnum.bnf',
  'findlink',
  'msrbot',
  'panscient',
  'yacybot',
  'AISearchBot',
  'IOI',
  'ips-agent',
  'tagoobot',
  'MJ12bot',
  'dotbot',
  'woriobot',
  'yanga',
  'buzzbot',
  'mlbot',
  'yandexbot',
  'purebot',
  'Linguee Bot',
  'Voyager',
  'CyberPatrol',
  'voilabot',
  'baiduspider',
  'citeseerxbot',
  'spbot',
  'twengabot',
  'postrank',
  'turnitinbot',
  'scribdbot',
  'page2rss',
  'sitebot',
  'linkdex',
  'Adidxbot',
  'blekkobot',
  'ezooms',
  'Mail.RU_Bot',
  'discobot',
  'heritrix',
  'findthatfile',
  'europarchive.org',
  'NerdByNature.Bot',
  'sistrix crawler',
  'ahrefsbot',
  'Aboundex',
  'domaincrawler',
  'wbsearchbot',
  'summify',
  'ccbot',
  'edisterbot',
  'seznambot',
  'ec2linkfinder',
  'gslfbot',
  'aihitbot',
  'intelium_bot',
  'facebookexternalhit',
  'yeti',
  'RetrevoPageAnalyzer',
  'lb-spider',
  'sogou',
  'lssbot',
  'careerbot',
  'wotbox',
  'wocbot',
  'ichiro',
  'DuckDuckBot',
  'lssrocketcrawler',
  'drupact',
  'webcompanycrawler',
  'acoonbot',
  'openindexspider',
  'gnam gnam spider',
  'web-archive-net.com.bot',
  'backlinkcrawler',
  'coccoc',
  'integromedb',
  'content crawler spider',
  'toplistbot',
  'seokicks-robot',
  'it2media-domain-crawler',
  'ip-web-crawler.com',
  'siteexplorer.info',
  'elisabot',
  'proximic',
  'changedetection',
  'blexbot',
  'arabot',
  'WeSEE:Search',
  'niki-bot',
  'CrystalSemanticsBot',
  'rogerbot',
  '360Spider',
  'psbot',
  'InterfaxScanBot',
  'Lipperhey SEO Service',
  'CC Metadata Scaper',
  'g00g1e.net',
  'GrapeshotCrawler',
  'urlappendbot',
  'brainobot',
  'fr-crawler',
  'binlar',
  'SimpleCrawler',
  'Livelapbot',
  'Twitterbot',
  'cXensebot',
  'smtbot',
  'bnf.fr_bot',
  'A6-Indexer',
  'ADmantX',
  'Facebot',
  'OrangeBot',
  'memorybot',
  'AdvBot',
  'MegaIndex',
  'SemanticScholarBot',
  'ltx71',
  'nerdybot',
  'xovibot',
  'BUbiNG',
  'Qwantify',
  'archive.org_bot',
  'Applebot',
  'TweetmemeBot',
  'crawler4j',
  'findxbot',
  'SemrushBot',
  'yoozBot',
  'lipperhey',
  'y!j-asr',
  'Domain Re-Animator Bot',
  'AddThis',
  'YisouSpider',
  'PetalBot',
] as const

const GOOGLE_RENDERER_RE = new RegExp(GOOGLE_RENDERER_TOKENS.join('|'), 'i')

const BOT_RE = new RegExp(
  `(${[...GOOGLE_RENDERER_TOKENS, ...BOT_TOKENS].join('|')})`,
  'i'
)

/** True for the agents that render pages on Google's behalf. */
export const isGoogleRenderer = (userAgent: string) =>
  GOOGLE_RENDERER_RE.test(userAgent)

/** True for known automated traffic. Deliberately over-inclusive. */
export const isBot = (userAgent: string) => BOT_RE.test(userAgent)
